/**
 * Engagement cadences — ADR-001 §8.7 (guide §6.7), §11.8. View tier: every state is computed on read from
 * `engagement_log` + `engagement_activities` + `clients.is_new_client`; nothing here is a stored number.
 *
 * Visibility follows the client (managers all; an ops_member the clients they are the current SPOC of;
 * requesters and HR none — out-of-scope ids are 404). No revenue appears in any engagement payload.
 */
import { z } from 'zod';
import { IsoDate, Timestamp, Uuid, list } from './common.js';
import { ClientRef, Segment } from './clients.js';
import { Task } from './tasks.js';
import { UserRef } from './users.js';
// ---------------------------------------------------------------- the eight activities
/** The eight activities of guide §6.7, seeded exactly. Keys are fixed; names and cadences are editable. */
export const ENGAGEMENT_ACTIVITY_KEYS = ['wa', 'meet', 'train', 'diy', 'testi', 'theme', 'mis', 'stake'];
export const ActivityKey = z.enum(ENGAGEMENT_ACTIVITY_KEYS).meta({ id: 'ActivityKey' });
/** Activities whose log needs a note: the MIS "What do you suggest next?" and the stakeholder update. */
export const NOTE_REQUIRED_ACTIVITIES = ['mis', 'stake'];
/** The log pop-up's note field per activity (prototype `engDo`): label and the message when it is missing. */
export const ENGAGEMENT_NOTE_RULE = {
    mis: { label: 'What we suggested next', missing: 'Say what you suggested next. An MIS alone is not enough.' },
    stake: { label: 'What improved and where we can improve together', missing: 'Say what improved and where we can improve together' },
};
/** The seeded names and cadences (prototype ENGA; ADR §8.7). Names are editable in Settings. */
export const DEFAULT_ENGAGEMENT_ACTIVITIES = [
    { key: 'wa', name: 'WhatsApp group', cadence_days: 2, new_client_cadence_days: null },
    { key: 'meet', name: 'Client meeting', cadence_days: 30, new_client_cadence_days: null },
    { key: 'train', name: 'Training', cadence_days: 30, new_client_cadence_days: 15 },
    { key: 'diy', name: 'DIY video shared', cadence_days: 30, new_client_cadence_days: null },
    { key: 'testi', name: 'Video testimonial', cadence_days: 30, new_client_cadence_days: null },
    { key: 'theme', name: 'New look and feel', cadence_days: 60, new_client_cadence_days: null },
    { key: 'mis', name: 'MIS with next steps', cadence_days: 30, new_client_cadence_days: null },
    { key: 'stake', name: 'Stakeholder update', cadence_days: 30, new_client_cadence_days: null },
];
export const EngagementActivity = z
    .object({
    key: ActivityKey,
    name: z.string(),
    /** Days between two of these for an established client. */
    cadence_days: z.number().int().min(1).max(365),
    /** Cadence for a new client (`since` in 2026); `null` = same as `cadence_days`. Seeded only for training (15). */
    new_client_cadence_days: z.number().int().min(1).max(365).nullable(),
    sort_order: z.number().int(),
    /** An inactive activity is hidden from the grid, the brief and "add what is due". */
    active: z.boolean(),
    /** The column head's rhythm in words: "every 2 days", "every month", "every 1 to 2 months". */
    beat: z.string(),
    /** True for `mis` and `stake`: logging one needs a note. */
    note_required: z.boolean(),
    updated_at: Timestamp,
})
    .meta({ id: 'EngagementActivity' });
export const EngagementActivityList = list(EngagementActivity).meta({ id: 'EngagementActivityList' });
export const ActivityKeyParams = z.object({ key: ActivityKey });
const Cadence = z.number().int().min(1, 'A cadence is at least 1 day').max(365, 'A cadence is at most 365 days');
/** PATCH /engagement/activities/{key} — Settings. Unversioned (a settings row: last write wins, audited). */
export const UpdateEngagementActivityBody = z
    .object({
    name: z.string().trim().min(2, 'Give the activity a name of at least 2 characters').max(60).optional(),
    cadence_days: Cadence.optional(),
    /** `null` clears the new-client cadence (new clients then use `cadence_days`). */
    new_client_cadence_days: Cadence.nullable().optional(),
    active: z.boolean().optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one thing' });
// ---------------------------------------------------------------- one cell (client × activity)
/**
 * `overdue` (red) when `next_due_on` is before today, or the activity was never done (booked or not);
 * `due_soon` (amber) when it is today or within `engagement.due_soon_days` (3); `on_track` (green) otherwise.
 */
export const EngagementState = z.enum(['overdue', 'due_soon', 'on_track']).meta({ id: 'EngagementState' });
/** Where the activity was done. `whatsapp_link` = the user composed it and marked it sent — never "delivered". */
export const EngagementVia = z.enum(['in_app', 'whatsapp_link', 'meeting', 'other']).meta({ id: 'EngagementVia' });
export const EngagementCell = z
    .object({
    activity_key: ActivityKey,
    name: z.string(),
    /** The cadence that applies to THIS client (the new-client cadence when it is new). */
    cadence_days: z.number().int().positive(),
    last_done_on: IsoDate.nullable(),
    /**
     * The booking, while it counts: dated after `last_done_on` (or nothing done yet); `null` once it is on or
     * before the last done date. When it counts it IS the next due date, earlier or later than the cadence gives.
     */
    booked_for: IsoDate.nullable(),
    /**
     * `booked_for` when it counts, else `last_done_on + cadence_days`. `null` = never done, EVEN IF booked: a
     * never-done cell is overdue with no date (prototype engDue), and the booking only shows in `sub_label`.
     */
    next_due_on: IsoDate.nullable(),
    state: EngagementState,
    /** Days from today to `next_due_on`: negative when overdue, 0 = due today; `null` with `next_due_on`. */
    days: z.number().int().nullable(),
    /** The chip wording: "Overdue 4d", "Due today", "In 2d", "On track"; "Overdue" when never done. */
    label: z.string(),
    /** The line under it: "Never done", "Done today", "Done 12d ago", plus ", booked 21 Oct" when `booked_for` is set. */
    sub_label: z.string(),
    /** The last log's note (the MIS suggestion, the stakeholder update), shown on hover. */
    last_note: z.string().nullable(),
    /** The open engagement task for this cell, if one was added to the task list. */
    open_task_id: Uuid.nullable(),
})
    .meta({ id: 'EngagementCell' });
/** GET /clients/{id}/engagement — the eight cells for one client, in activity order. */
export const ClientEngagement = z
    .object({
    client: ClientRef,
    is_new_client: z.boolean(),
    as_of: IsoDate,
    cells: z.array(EngagementCell),
    /**
     * `overdue`/`due_soon` count cells by state; `on_track` is every cell NOT overdue (green and amber — the
     * prototype's engStats `ok`), so the row's "On track N of 8" chip is `on_track` of `total`.
     */
    summary: z.object({
        total: z.number().int().nonnegative(),
        overdue: z.number().int().nonnegative(),
        due_soon: z.number().int().nonnegative(),
        due_today: z.number().int().nonnegative(),
        on_track: z.number().int().nonnegative(),
    }),
})
    .meta({ id: 'ClientEngagement' });
/** GET /engagement/grid — the whole grid, one row per client in scope. */
export const EngagementGrid = z
    .object({
    as_of: IsoDate,
    /** The column heads, in order (active activities only). */
    activities: z.array(EngagementActivity),
    rows: z.array(ClientEngagement),
    /** The four stats: On track % (`on_track` = cells not overdue, of `total`), Overdue, Due today, Clients. Also the hub badge (`overdue`). */
    summary: z.object({
        total: z.number().int().nonnegative(),
        on_track: z.number().int().nonnegative(),
        on_track_pct: z.number().int().min(0).max(100),
        overdue: z.number().int().nonnegative(),
        due_today: z.number().int().nonnegative(),
        clients: z.number().int().nonnegative(),
    }),
})
    .meta({ id: 'EngagementGrid' });
export const EngagementGridQuery = z.object({
    segment: Segment.optional(),
    /** Only rows with at least one cell in this state (e.g. `overdue` for "what is late"). */
    state: EngagementState.optional(),
    /** The person filter ("Everyone" = omitted). Managers only; ignored for a member, who sees their own clients. */
    spoc_id: Uuid.optional(),
});
// ---------------------------------------------------------------- writes
export const EngagementCellParams = z.object({ id: Uuid, activity_key: ActivityKey });
/**
 * POST /clients/{id}/engagement/{activity_key}/log. `done_on` may not be in the future, nor before the client
 * started. `note` is required for `mis` and `stake` (`ENGAGEMENT_NOTE_RULE`) — enforced by the API with the
 * activity key from the path (a `422` on `note`). Logging closes any open engagement task for the cell (proof
 * "Recorded in the engagement calendar") and clears a booking on or before `done_on`.
 */
export const LogEngagementBody = z
    .object({
    done_on: IsoDate,
    note: z.string().trim().min(1).max(1000, 'Keep the note under 1,000 characters').optional(),
    via: EngagementVia.default('in_app'),
})
    .strict();
/** The note rule as code, so the SPA and the API apply the same one. */
export const engagementNoteMissing = (key, note) => NOTE_REQUIRED_ACTIVITIES.includes(key) && !(note && note.trim().length > 0);
export const EngagementLog = z
    .object({
    id: Uuid,
    client_id: Uuid,
    activity_key: ActivityKey,
    done_on: IsoDate,
    via: EngagementVia,
    note: z.string().nullable(),
    by: UserRef,
    at: Timestamp,
    /** The open engagement task this log closed, if any. */
    closed_task_id: Uuid.nullable(),
    /** The cell after the log. */
    cell: EngagementCell,
})
    .meta({ id: 'EngagementLog' });
/** POST /clients/{id}/engagement/{activity_key}/book — today or later; `null` clears the booking. */
export const BookEngagementBody = z.object({ booked_for: IsoDate.nullable() }).strict();
/**
 * POST /clients/{id}/engagement/tasks — "Add what is due to the task list". One task per due or overdue cell
 * (state `due_soon` or `overdue`, so the window is the `engagement.due_soon_days` setting — ADR §8.7; the
 * prototype's fixed "tomorrow or sooner" is not used), or only the listed ones, for the client's current SPOC:
 * title "{activity}, {client}", `due = max(today, next_due_on)`, `priority = more than 3 days overdue ? high :
 * medium`, `source = 'engage'`, `origin_ref` the cell. Idempotent per (client, activity): a cell that already
 * has an open engagement task is skipped, not duplicated.
 */
export const AddEngagementTasksBody = z
    .object({ activity_keys: z.array(ActivityKey).min(1).max(8).optional() })
    .strict()
    .refine((b) => !b.activity_keys || new Set(b.activity_keys).size === b.activity_keys.length, {
    message: 'List each activity once',
    path: ['activity_keys'],
});
export const EngagementSkipReason = z
    .enum(['already_open', 'not_due', 'inactive'])
    .meta({ id: 'EngagementSkipReason', description: 'already_open = an open engagement task exists; not_due = on track (not due within engagement.due_soon_days); inactive = switched off in Settings' });
export const AddEngagementTasksResponse = z
    .object({
    created: z.array(Task),
    skipped: z.array(z.object({ activity_key: ActivityKey, reason: EngagementSkipReason, task_id: Uuid.nullable() })),
})
    .meta({ id: 'AddEngagementTasksResponse' });
//# sourceMappingURL=engagement.js.map